import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";

import { useGetEatAppRestaurants, useGetEatAppStatus, useSaveEatAppMappings } from "hooks";
import { useToast } from "../../../context/ToastContext";
import { EatAppLocationMappingFields, RESTAURANT_REQUIRED } from "./EatAppLocationMappingFields";

export function EatAppMappingDialog({ isOpen, onOpenChange, organisationId, config }) {
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const [mappings, setMappings] = useState({});
  const { data: statusData } = useGetEatAppStatus(organisationId);
  const { data: restaurantsData, isLoading } = useGetEatAppRestaurants(
    organisationId,
    config?.config_id,
    isOpen
  );
  const saveMappings = useSaveEatAppMappings();
  const locations = statusData?.data?.available_locations || [];
  const restaurants = restaurantsData?.data?.restaurants || [];
  const selectedMappings = useMemo(() => locations
    .filter((location) => mappings[location.id] && mappings[location.id] !== RESTAURANT_REQUIRED)
    .map((location) => ({
      bconnect_location_id: location.id,
      restaurant_id: mappings[location.id],
    })), [locations, mappings]);
  const hasIncompleteMappings = locations.some((location) => mappings[location.id] === RESTAURANT_REQUIRED);

  useEffect(() => {
    if (!isOpen) return;
    setMappings(Object.fromEntries((config?.location_mappings || []).map((mapping) => [
      mapping.bconnect_location_id,
      mapping.restaurant_id,
    ])));
  }, [isOpen, config]);

  const save = async () => {
    if (hasIncompleteMappings) {
      setToastMessage("Choose an Eat App restaurant for every assigned location.");
      return;
    }
    if (!selectedMappings.length) {
      setToastMessage("Map at least one BConnect location to an Eat App restaurant.");
      return;
    }
    const selectedIds = new Set(selectedMappings.map((mapping) => String(mapping.bconnect_location_id)));
    const movedCount = (statusData?.data?.configs || [])
      .filter((item) => item.config_id !== config.config_id)
      .flatMap((item) => item.location_mappings || [])
      .filter((mapping) => selectedIds.has(String(mapping.bconnect_location_id))).length;
    if (movedCount && !window.confirm(
      `${movedCount} location(s) will move from another Eat App group. Continue?`
    )) return;

    try {
      await saveMappings.mutateAsync({
        organisation_id: organisationId,
        config_id: config.config_id,
        location_mappings: selectedMappings,
      });
      queryClient.invalidateQueries({ queryKey: ["eat-app-status"] });
      setToastMessage("Eat App location mappings saved.");
      onOpenChange(false);
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Could not save Eat App mappings.");
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="680px" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <Dialog.Title>Map Eat App group {config?.group_id}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Map each BConnect location to a restaurant in this Eat App group. Brand sections help you select locations together.
        </Dialog.Description>
        {isLoading ? <Text color="gray">Loading restaurants...</Text> : (
          <EatAppLocationMappingFields
            organisationId={organisationId}
            configId={config?.config_id}
            locations={locations}
            restaurants={restaurants}
            mappings={mappings}
            setMappings={setMappings}
            configs={statusData?.data?.configs || []}
          />
        )}
        <Flex justify="end" gap="3" mt="5">
          <Button variant="soft" color="gray" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} loading={saveMappings.isPending} disabled={isLoading || !restaurants.length || hasIncompleteMappings}>Save mappings</Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
